import { useState, useRef, useCallback, useEffect } from 'react'
import { clsx } from 'clsx'
import { Chart } from '../components/Chart'
import { DrawingTools } from '../components/DrawingTools'
import { IndicatorPanel } from '../components/IndicatorPanel'
import { SymbolSearch } from '../components/SymbolSearch'
import { TimeframeSelector } from '../components/TimeframeSelector'
import { ChartTypeSelector } from '../components/ChartTypeSelector'
import { ChartToolbar } from '../components/Chart/ChartToolbar'
import { useChartStore } from '../store/chartStore'
import type { ChartType, Timeframe, DrawingTool, IndicatorConfig } from '../types'

// TradingView-quality charting page
export default function ChartPage() {
  const [showIndicators, setShowIndicators] = useState(false)
  const [showWatchlist, setShowWatchlist] = useState(true)
  const [showOrderPanel, setShowOrderPanel] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [layoutMode, setLayoutMode] = useState<'single' | 'split-h' | 'split-v' | 'quad'>('single')
  const [activePane, setActivePane] = useState(0)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  const {
    symbol,
    setSymbol,
    chartType,
    setChartType,
    timeframe,
    setTimeframe,
    indicators,
    addIndicator,
    removeIndicator,
    updateIndicator,
    activeDrawingTool,
    setActiveDrawingTool,
    currentPrice,
    priceChange24h,
    wsConnected,
  } = useChartStore()

  // Fullscreen toggle
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      chartContainerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }, [])

  // Screenshot
  const handleSnapshot = useCallback(() => {
    // Chart screenshot logic
    const canvas = chartContainerRef.current?.querySelector('canvas')
    if (canvas) {
      const link = document.createElement('a')
      link.download = `${symbol.replace('/', '-')}_${timeframe}_${Date.now()}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
  }, [symbol, timeframe])

  // Copy to clipboard
  const handleCopyToClipboard = useCallback(async () => {
    const canvas = chartContainerRef.current?.querySelector('canvas')
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ])
        }
      })
    }
  }, [])

  // Share
  const handleShare = useCallback(() => {
    const url = `${window.location.origin}/chart?symbol=${encodeURIComponent(symbol)}&tf=${timeframe}`
    navigator.share?.({ title: `${symbol} Chart`, url }) || navigator.clipboard.writeText(url)
  }, [symbol, timeframe])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return

      switch (e.key) {
        case 'Escape':
          setActiveDrawingTool('cursor')
          break
        case 'i':
          setShowIndicators(!showIndicators)
          break
        case 'f':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            // Focus symbol search
          }
          break
        case '1': setTimeframe('1m'); break
        case '2': setTimeframe('5m'); break
        case '3': setTimeframe('15m'); break
        case '4': setTimeframe('1h'); break
        case '5': setTimeframe('4h'); break
        case '6': setTimeframe('1d'); break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showIndicators, setTimeframe, setActiveDrawingTool])

  // Popular watchlist symbols
  const watchlistSymbols = [
    { symbol: 'BTC/USDT', name: 'Bitcoin', price: 97245.00, change: 2.34 },
    { symbol: 'ETH/USDT', name: 'Ethereum', price: 3420.50, change: 1.82 },
    { symbol: 'SOL/USDT', name: 'Solana', price: 195.30, change: 5.21 },
    { symbol: 'BNB/USDT', name: 'BNB', price: 615.80, change: -0.45 },
    { symbol: 'XRP/USDT', name: 'Ripple', price: 2.45, change: 3.12 },
    { symbol: 'ADA/USDT', name: 'Cardano', price: 0.98, change: 1.55 },
    { symbol: 'EUR/USD', name: 'Euro/Dollar', price: 1.0845, change: -0.12 },
    { symbol: 'GBP/USD', name: 'Pound/Dollar', price: 1.2680, change: 0.08 },
  ]

  return (
    <div className="h-screen bg-kit-darker flex flex-col overflow-hidden">
      {/* Top Toolbar */}
      <div className="h-14 border-b border-kit-border bg-kit-card flex items-center px-4 gap-4">
        {/* Symbol Search */}
        <div className="flex items-center gap-2">
          <SymbolSearch
            value={symbol}
            onChange={setSymbol}
          />
          {/* Price Display */}
          <div className="ml-4">
            <div className="text-lg font-bold text-white">
              {currentPrice > 0 ? currentPrice.toLocaleString(undefined, { 
                minimumFractionDigits: 2,
                maximumFractionDigits: symbol.includes('BTC') ? 2 : 4 
              }) : '—'}
            </div>
            <div className={clsx(
              'text-xs font-medium',
              priceChange24h >= 0 ? 'text-kit-green' : 'text-kit-red'
            )}>
              {priceChange24h >= 0 ? '+' : ''}{priceChange24h.toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Timeframe Selector */}
        <div className="flex items-center border-l border-kit-border pl-4">
          <TimeframeSelector
            value={timeframe}
            onChange={setTimeframe}
          />
        </div>

        {/* Chart Type */}
        <div className="flex items-center border-l border-kit-border pl-4">
          <ChartTypeSelector
            value={chartType}
            onChange={setChartType}
          />
        </div>

        {/* Indicators Button */}
        <button
          onClick={() => setShowIndicators(!showIndicators)}
          className={clsx(
            'px-3 py-1.5 rounded text-sm font-medium flex items-center gap-2 transition-colors',
            showIndicators
              ? 'bg-kit-accent text-black'
              : 'bg-kit-darker text-gray-300 hover:text-white border border-kit-border'
          )}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Indicators
          {indicators.length > 0 && (
            <span className="bg-kit-green text-black text-xs px-1.5 rounded-full">
              {indicators.length}
            </span>
          )}
        </button>

        {/* Layout Buttons */}
        <div className="flex items-center gap-1 border-l border-kit-border pl-4">
          <button
            onClick={() => setLayoutMode('single')}
            className={clsx(
              'p-1.5 rounded transition-colors',
              layoutMode === 'single' ? 'bg-kit-accent/20 text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Single Chart"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="2" y="2" width="12" height="12" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => setLayoutMode('split-h')}
            className={clsx(
              'p-1.5 rounded transition-colors',
              layoutMode === 'split-h' ? 'bg-kit-accent/20 text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Horizontal Split"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="2" y="2" width="5" height="12" rx="1" />
              <rect x="9" y="2" width="5" height="12" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => setLayoutMode('split-v')}
            className={clsx(
              'p-1.5 rounded transition-colors',
              layoutMode === 'split-v' ? 'bg-kit-accent/20 text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Vertical Split"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="2" y="2" width="12" height="5" rx="1" />
              <rect x="2" y="9" width="12" height="5" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => setLayoutMode('quad')}
            className={clsx(
              'p-1.5 rounded transition-colors',
              layoutMode === 'quad' ? 'bg-kit-accent/20 text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Quad View"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="2" y="2" width="5" height="5" rx="1" />
              <rect x="9" y="2" width="5" height="5" rx="1" />
              <rect x="2" y="9" width="5" height="5" rx="1" />
              <rect x="9" y="9" width="5" height="5" rx="1" />
            </svg>
          </button>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right Side Controls */}
        <div className="flex items-center gap-2">
          {/* Connection Status */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className={clsx(
              'w-2 h-2 rounded-full',
              wsConnected ? 'bg-kit-green' : 'bg-kit-red'
            )} />
            {wsConnected ? 'Live' : 'Offline'}
          </div>

          {/* Toggle Panels */}
          <button
            onClick={() => setShowWatchlist(!showWatchlist)}
            className={clsx(
              'p-2 rounded transition-colors',
              showWatchlist ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Toggle Watchlist"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>

          <button
            onClick={() => setShowOrderPanel(!showOrderPanel)}
            className={clsx(
              'p-2 rounded transition-colors',
              showOrderPanel ? 'text-kit-accent' : 'text-gray-400 hover:text-white'
            )}
            title="Toggle Order Panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8l-1.35 2.7a1 1 0 00.9 1.5h12M7 13l-2.293-6H21m-14 6V6.5" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Drawing Tools Sidebar */}
        <DrawingTools
          activeTool={activeDrawingTool}
          onToolSelect={setActiveDrawingTool}
        />

        {/* Watchlist Panel */}
        {showWatchlist && (
          <div className="w-64 border-r border-kit-border bg-kit-card flex flex-col">
            <div className="p-3 border-b border-kit-border flex items-center justify-between">
              <h3 className="font-medium text-white">Watchlist</h3>
              <button className="text-gray-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {watchlistSymbols.map((item) => (
                <button
                  key={item.symbol}
                  onClick={() => setSymbol(item.symbol)}
                  className={clsx(
                    'w-full px-3 py-2 flex items-center justify-between hover:bg-kit-darker transition-colors',
                    symbol === item.symbol && 'bg-kit-accent/10 border-l-2 border-kit-accent'
                  )}
                >
                  <div className="text-left">
                    <div className="text-sm font-medium text-white">{item.symbol}</div>
                    <div className="text-xs text-gray-500">{item.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">
                      {item.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className={clsx(
                      'text-xs',
                      item.change >= 0 ? 'text-kit-green' : 'text-kit-red'
                    )}>
                      {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chart Area */}
        <div className="flex-1 flex flex-col relative" ref={chartContainerRef}>
          {/* Chart Toolbar */}
          <ChartToolbar
            onSnapshot={handleSnapshot}
            onCopyToClipboard={handleCopyToClipboard}
            onShare={handleShare}
            onFullscreen={handleFullscreen}
            isFullscreen={isFullscreen}
          />

          {/* Chart Grid */}
          {layoutMode === 'single' && (
            <div className="flex-1">
              <Chart
                symbol={symbol}
                chartType={chartType}
                timeframe={timeframe}
                indicators={indicators}
                activeDrawingTool={activeDrawingTool}
              />
            </div>
          )}

          {layoutMode === 'split-h' && (
            <div className="flex-1 flex">
              <div className="flex-1 border-r border-kit-border">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe={timeframe}
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
              <div className="flex-1">
                <Chart
                  symbol="ETH/USDT"
                  chartType={chartType}
                  timeframe={timeframe}
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
            </div>
          )}

          {layoutMode === 'split-v' && (
            <div className="flex-1 flex flex-col">
              <div className="flex-1 border-b border-kit-border">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe={timeframe}
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
              <div className="flex-1">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe="1d"
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
            </div>
          )}

          {layoutMode === 'quad' && (
            <div className="flex-1 grid grid-cols-2 grid-rows-2">
              <div className="border-r border-b border-kit-border">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe="15m"
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
              <div className="border-b border-kit-border">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe="1h"
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
              <div className="border-r border-kit-border">
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe="4h"
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
              <div>
                <Chart
                  symbol={symbol}
                  chartType={chartType}
                  timeframe="1d"
                  indicators={indicators}
                  activeDrawingTool={activeDrawingTool}
                />
              </div>
            </div>
          )}

          {/* Active Indicators Bar */}
          {indicators.length > 0 && (
            <div className="absolute top-16 left-4 flex flex-wrap gap-2 z-10">
              {indicators.map((ind) => (
                <div
                  key={ind.id}
                  className="px-2 py-1 bg-kit-darker/90 backdrop-blur-sm rounded border border-kit-border flex items-center gap-2 text-xs"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: ind.color }}
                  />
                  <span className="text-gray-300">{ind.type}</span>
                  <button
                    onClick={() => removeIndicator(ind.id)}
                    className="text-gray-500 hover:text-white ml-1"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Panel */}
        {showOrderPanel && (
          <div className="w-80 border-l border-kit-border bg-kit-card flex flex-col">
            <div className="p-3 border-b border-kit-border">
              <h3 className="font-medium text-white">Place Order</h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Order Type */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Order Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button className="px-3 py-2 bg-kit-green/20 text-kit-green border border-kit-green/30 rounded font-medium">
                    Buy
                  </button>
                  <button className="px-3 py-2 bg-kit-darker text-gray-400 border border-kit-border rounded hover:text-white">
                    Sell
                  </button>
                </div>
              </div>

              {/* Order Mode */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Mode</label>
                <select className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded text-white">
                  <option>Market</option>
                  <option>Limit</option>
                  <option>Stop</option>
                  <option>Stop Limit</option>
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded text-white pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {symbol.split('/')[0]}
                  </span>
                </div>
              </div>

              {/* Slider */}
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-2">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  defaultValue="50"
                  className="w-full accent-kit-accent"
                />
              </div>

              {/* Stop Loss / Take Profit */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Stop Loss</label>
                  <input
                    type="number"
                    placeholder="—"
                    className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Take Profit</label>
                  <input
                    type="number"
                    placeholder="—"
                    className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded text-white"
                  />
                </div>
              </div>

              {/* Submit */}
              <button className="w-full py-3 bg-kit-green text-black font-bold rounded hover:bg-kit-green/80 transition-colors">
                Buy {symbol.split('/')[0]}
              </button>

              {/* Available Balance */}
              <div className="text-center text-xs text-gray-500">
                Available: 10,000.00 USDT
              </div>
            </div>
          </div>
        )}

        {/* Indicator Panel Modal */}
        {showIndicators && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-kit-card w-full max-w-4xl max-h-[80vh] rounded-xl border border-kit-border overflow-hidden">
              <div className="p-4 border-b border-kit-border flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Indicators</h2>
                <button
                  onClick={() => setShowIndicators(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="h-[60vh] overflow-hidden">
                <IndicatorPanel
                  activeIndicators={indicators}
                  onAddIndicator={(config) => addIndicator(config.type, config.params)}
                  onRemoveIndicator={removeIndicator}
                  onUpdateIndicator={updateIndicator}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Status Bar */}
      <div className="h-6 border-t border-kit-border bg-kit-card flex items-center px-4 text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>K.I.T. Charts v1.0</span>
          <span>•</span>
          <span>{new Date().toLocaleString()}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 bg-kit-darker rounded">ESC</kbd> Cancel Drawing
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 bg-kit-darker rounded">I</kbd> Indicators
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 bg-kit-darker rounded">1-6</kbd> Timeframes
          </span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-4">
          <span>Powered by K.I.T. Bot</span>
        </div>
      </div>
    </div>
  )
}
